import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import type { QuotationFormGroupRow } from '../../quotationFormGroups';
import {
  buildGroupRecurringInput,
  computeRecurringEventDates,
  formatQuotationRecurringDate,
  quotationFormGroupContractEndDate,
  quotationFormGroupTotalRecurringCount,
  recurringIntervalLabel,
  type QuotationContractHeaderInput,
} from '../../quotationRecurring';

type Props = {
  group: QuotationFormGroupRow;
  contractHeader: QuotationContractHeaderInput;
};

const PREVIEW_SCHEDULE_LIMIT = 5;

/** Ringkasan derived recurring kontrak di tab Sampel (N). */
export function QuotationFormGroupRecurringSummary({ group, contractHeader }: Props) {
  const [datesOpen, setDatesOpen] = useState(false);
  const summary = useMemo(() => {
    const endDate = quotationFormGroupContractEndDate(contractHeader);
    const totalCount = quotationFormGroupTotalRecurringCount(group, contractHeader);
    const input = buildGroupRecurringInput(group, contractHeader);
    const allDates = input ? computeRecurringEventDates(input) : [];
    const previewDates = allDates.slice(0, PREVIEW_SCHEDULE_LIMIT);
    const interval = Number.parseInt(group.recurring_interval.trim() || '1', 10);
    const rt = group.recurring_type === 'triwulan' ? 'monthly' : group.recurring_type;
    return {
      endDate,
      totalCount,
      allDates,
      previewDates,
      intervalLabel:
        rt === 'weekly' || rt === 'monthly' || rt === 'yearly'
          ? recurringIntervalLabel(rt, Number.isFinite(interval) && interval >= 1 ? interval : 1)
          : null,
      hasOverride: Boolean(group.recurring_count_override.trim()),
      hasGroupStart: Boolean(group.recurring_start_date.trim()),
    };
  }, [group, contractHeader]);

  if (!summary.totalCount && !summary.endDate) {
    return (
      <Alert severity="info" data-testid="quotation-form-group-recurring-summary">
        Lengkapi periode kontrak di tab Detail dan jenis perulangan grup untuk melihat ringkasan jadwal.
      </Alert>
    );
  }

  return (
    <>
    <Alert severity="info" data-testid="quotation-form-group-recurring-summary">
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" fontWeight={600}>
          Ringkasan kontrak (group ini)
        </Typography>
        {summary.endDate ? (
          <Typography variant="body2">
            Akhir kontrak: <strong>{formatQuotationRecurringDate(summary.endDate)}</strong>
          </Typography>
        ) : null}
        {summary.intervalLabel ? (
          <Typography variant="body2">
            Frekuensi: <strong>{summary.intervalLabel}</strong>
            {summary.hasGroupStart ? ' · mulai dari tanggal group' : ' · mulai dari tanggal kontrak header'}
          </Typography>
        ) : null}
        {summary.totalCount != null ? (
          <Typography variant="body2">
            Total jadwal berulang: <strong>{summary.totalCount}</strong>
            {summary.hasOverride ? ' (override manual)' : ' (dihitung dari periode kontrak)'}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          Subtotal per baris sampel = jumlah titik × harga × total jadwal berulang
          {summary.totalCount != null ? ` (${summary.totalCount}×)` : ''}.
        </Typography>
        {summary.previewDates.length > 0 ? (
          <Typography variant="caption" color="text.secondary" component="div">
            Preview jadwal:{' '}
            {summary.previewDates.map((d) => formatQuotationRecurringDate(d)).join(', ')}
            {summary.allDates.length > summary.previewDates.length ? (
              <>
                {' … '}
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setDatesOpen(true)}
                  data-testid="quotation-form-group-recurring-read-more"
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Read more
                </Link>
              </>
            ) : null}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
      <Dialog
        open={datesOpen}
        onClose={() => setDatesOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="quotation-recurring-dates-title"
      >
        <DialogTitle id="quotation-recurring-dates-title">
          Jadwal sampling ({summary.allDates.length})
        </DialogTitle>
        <DialogContent dividers>
          <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            {summary.allDates.map((d, index) => (
              <Typography key={`${d.getTime()}-${index}`} component="li" variant="body2">
                {formatQuotationRecurringDate(d)}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDatesOpen(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
